import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Images, Lock, MoreHorizontal } from 'lucide-react';
import { ActionMenu, Button, Pill, TokenText, type ActionMenuItem } from '@/shared/components';
import { useApi, type ArrivalStepResponse } from '@/shared/api';

const ORIGIN_LABEL: Record<ArrivalStepResponse['origin'], string> = {
  Inherited: 'Vom Objekt',
  Overridden: 'Überschrieben',
  Apartment: 'Nur hier',
};

interface Props {
  step: ArrivalStepResponse;
  number: number;
  /** Am Objekt ist alles beweg- und löschbar, am Apartment nur die eigenen Schritte. */
  scope: 'object' | 'apartment';
  first: boolean;
  last: boolean;
  busy: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onMove: (direction: 'Up' | 'Down') => void;
  onDelete: () => void;
  onResetOverride: () => void;
  imagesOpen: boolean;
  onToggleImages: () => void;
  /** Der Bild-Bereich dieses Schritts, wenn er aufgeklappt ist. */
  footer?: ReactNode;
}

/** Ein Schritt als Karte: Nummer im Weg, Text, Zustände — Bearbeiten und ein Menü für den Rest. */
export function ArrivalStepCard(props: Props) {
  const { step, scope, busy } = props;
  const own = scope === 'object' || step.origin === 'Apartment';
  const api = useApi();
  // Derselbe Schlüssel wie im Bild-Bereich: aufgeklappt wird nicht noch einmal geladen.
  const images = useQuery({
    queryKey: ['arrival-steps', step.id, 'images'],
    queryFn: () => api.arrivalSteps.images.list(step.id),
  });
  const count = images.data?.images.length;
  const marked = images.data?.images.some((image) => image.annotations.length > 0);
  const menu: ActionMenuItem[] = [
    {
      label: 'Nach oben',
      disabled: busy || props.first || !own,
      onClick: () => props.onMove('Up'),
    },
    {
      label: 'Nach unten',
      disabled: busy || props.last || !own,
      onClick: () => props.onMove('Down'),
    },
    { separator: true },
    step.origin === 'Overridden' && scope === 'apartment'
      ? {
          label: 'Überschreibung zurücknehmen',
          icon: 'Refresh',
          disabled: busy,
          onClick: props.onResetOverride,
        }
      : {
          label: 'Löschen',
          icon: 'Trash',
          destructive: true,
          disabled: busy || !own,
          onClick: props.onDelete,
        },
  ];

  return (
    <li className="@container py-4 first:pt-0 last:pb-0">
      <div className="@lg:grid-cols-[2rem_minmax(0,1fr)_auto] grid grid-cols-[2rem_minmax(0,1fr)] gap-x-3 gap-y-3">
        <span
          aria-hidden
          className="bg-navy text-paper grid size-8 place-items-center rounded-full text-[13px] font-bold tabular-nums"
        >
          {props.number}
        </span>
        <div className="min-w-0">
          <p className="text-ink text-[15px] font-semibold">{step.titleDe}</p>
          <p className="text-ink-soft mt-1 whitespace-pre-wrap text-sm leading-relaxed">
            <TokenText text={step.bodyDe} />
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {scope === 'apartment' && <Pill size="sm">{ORIGIN_LABEL[step.origin]}</Pill>}
            {step.isTimeLocked && (
              <Pill variant="warning" size="sm">
                <Lock size={11} strokeWidth={2} aria-hidden />
                Code erst 12 h vor Anreise
              </Pill>
            )}
            {!step.titleEn && !step.bodyEn && (
              <Pill variant="outline" size="sm">
                nur deutsch
              </Pill>
            )}
            <button
              type="button"
              aria-expanded={props.imagesOpen}
              onClick={props.onToggleImages}
              className="border-line text-ink hover:border-sage focus-visible:outline-sage inline-flex min-h-11 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold focus-visible:outline focus-visible:outline-2"
            >
              <Images size={13} strokeWidth={2} aria-hidden />
              {!count ? 'Bilder' : count === 1 ? '1 Bild' : `${count} Bilder`}
              {marked && <span className="text-ink-soft font-normal">mit Markierung</span>}
            </button>
          </div>
        </div>
        {props.canEdit && (
          <div className="@lg:col-start-3 col-start-2 flex items-start gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="min-h-11"
              disabled={busy}
              onClick={props.onEdit}
            >
              Bearbeiten
            </Button>
            <ActionMenu
              items={menu}
              trigger={
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="min-h-11 min-w-11"
                  aria-label={`Weitere Aktionen für ${step.titleDe}`}
                >
                  <MoreHorizontal size={16} aria-hidden />
                </Button>
              }
            />
          </div>
        )}
      </div>
      {props.footer && <div className="border-line/60 mt-4 border-t pt-4">{props.footer}</div>}
    </li>
  );
}
